import { RefObject, useLayoutEffect, useRef } from 'react';

// A popover that opens beside its button can run off the edge of a narrow screen. While it is open, this nudges it
// sideways just enough to sit inside the window (with a small margin), and again if the window is resized.
// Attach the returned ref to the popover itself; it moves the popover with a transform, so layout is untouched.
export function useKeepInViewport<T extends HTMLElement = HTMLDivElement>(open: boolean, margin = 8): RefObject<T | null> {
  const ref = useRef<T | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!open || !el) return;
    const fit = () => {
      el.style.transform = ''; // where it would sit by itself
      const { left, right } = el.getBoundingClientRect();
      const width = document.documentElement.clientWidth;
      let dx = 0;
      if (right > width - margin) dx = width - margin - right;
      if (left + dx < margin) dx = margin - left;
      if (dx) el.style.transform = `translateX(${Math.round(dx)}px)`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => {
      window.removeEventListener('resize', fit);
      el.style.transform = '';
    };
  }, [open, margin]);

  return ref;
}
